// src/components/Implement/ImplementComponent.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, ListGroup } from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { useUserContext } from '../Context/UserContext';
import outputs from '../../../amplify_outputs.json';

type Issue = Schema['Issue']['type'];
type Profile = Schema['Profile']['type'];

/** ============ External endpoint from outputs (already the full enqueue URL) ============ */
const PROPOSE_STRATEGIES_ENQUEUE = String(
  (outputs as any)?.custom?.proposeStrategiesApiUrl || ''
).trim();

/** ============ Tiny utils ============ */
function unwrap<T>(maybeWrapped: unknown): T | null {
  if (maybeWrapped && typeof maybeWrapped === 'object' && 'data' in (maybeWrapped as any)) {
    const { data } = maybeWrapped as { data: T | null };
    return data ?? null;
  }
  return (maybeWrapped as T) ?? null;
}
function isNonEmptyString(x: unknown): x is string {
  return typeof x === 'string' && x.trim().length > 0;
}
function coerceStrings(arr?: (string | null)[] | null): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
}
const safe = (s?: string | null) => (typeof s === 'string' ? s.trim() : '');
const lower = (s?: string | null) => safe(s).toLowerCase();

function isInitiator(me: string | null | undefined, issue: Issue | null): boolean {
  return !!me && !!issue && lower(me) === lower(issue.initiatorEmail);
}
function isCollaborator(me: string | null | undefined, issue: Issue | null): boolean {
  return !!me && !!issue && lower(me) === lower(issue.collaboratorEmail);
}

/** ============ Neutral Solution helpers ============ */
type NeutralSolution = { title: string; summary?: string; steps?: string[] };

function parseBestSolutionJson(x: unknown): any | null {
  if (!x) return null;
  if (typeof x === 'object') return x as any;
  if (typeof x === 'string') {
    try {
      return JSON.parse(x);
    } catch {
      return null;
    }
  }
  return null;
}
function cleanSummary(summary: string | undefined): string | undefined {
  if (!summary) return summary;
  return summary.replace(/\s*\([^)]*\)/g, '').replace(/\s+\./g, '.').trim();
}
function getNeutralSolutionFromIssue(issue: any): NeutralSolution | null {
  const raw = parseBestSolutionJson(issue?.bestSolutionJson);
  if (!raw) return null;

  const chosenId: string | undefined = raw.chosenId ?? raw.normalized?.id;
  const proposals: any[] = Array.isArray(raw.proposals) ? raw.proposals : [];
  const fromChosen = chosenId ? proposals.find((p) => p?.id === chosenId) : null;

  const src = fromChosen ?? raw.normalized ?? raw.raw ?? null;
  if (!src || typeof src !== 'object') return null;

  const title =
    typeof src.title === 'string' && src.title.trim() ? src.title.trim() : 'Proposed Solution';
  const summary =
    typeof src.summary === 'string' && src.summary.trim() ? cleanSummary(src.summary) : undefined;
  const steps = Array.isArray(src.steps)
    ? src.steps.filter((s: any) => typeof s === 'string' && s.trim())
    : undefined;

  return { title, summary, steps };
}

export default function ImplementComponent() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { email: userEmail, firstName } = useUserContext();

  const sessionId = useMemo(
    () => decodeURIComponent(id ?? '').trim().replace(/\/+$/, ''),
    [id]
  );

  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState<Issue | null>(null);
  const [error, setError] = useState<string | null>(null);

  // DCS Status State
  const [myDcsMode, setMyDcsMode] = useState<string | null>(null);
  const [partnerDcsMode, setPartnerDcsMode] = useState<string | null>(null);

  // enqueue guard
  const enqueuedRef = useRef(false);
  const mounted = useRef(true);

  /** Track latest issue for polling logic */
  const latestIssueRef = useRef<Issue | null>(null);
  useEffect(() => {
    latestIssueRef.current = issue;
  }, [issue]);

  /** Only track bestSolutionJson once per mount after first successful fetch */
  const loggedBestOnceRef = useRef(false);

  /** ============ Initial read (one-shot) ============ */
  async function readIssue(_logRaw = true) {
    const raw = (await client.models.Issue.get(
      { id: sessionId },
      { authMode: 'userPool' }
    )) as unknown;
    const data = unwrap<Issue>(raw);
    if (mounted.current) setIssue(data);

    if (!loggedBestOnceRef.current) {
      const bestSolutionJson = (data as any)?.bestSolutionJson ?? null;
      if (bestSolutionJson !== undefined) {
        loggedBestOnceRef.current = true;
      }
    }
    return data;
  }

  useEffect(() => {
    mounted.current = true;
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        await readIssue(true);
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load session.');
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, [sessionId]);

  /** ============ observeQuery subscription ============ */
  useEffect(() => {
    if (!sessionId) return;
    const observable = (client.models.Issue as any).observeQuery?.({
      filter: { id: { eq: sessionId } },
      authMode: 'userPool',
    });

    if (!observable?.subscribe) return;

    const sub = observable.subscribe({
      next: (snapshot: any) => {
        const items: Issue[] = Array.isArray(snapshot?.items) ? snapshot.items : [];
        const row = items.find((x) => x.id === sessionId) ?? items[0] ?? null;
        if (row) {
          setIssue(row);
        }
      },
      error: (_e: any) => {},
    });

    return () => sub.unsubscribe();
  }, [sessionId]);

  /** ============ Role ============ */
  const meIsInitiator = useMemo(
    () => isInitiator(userEmail, issue),
    [userEmail, issue]
  );
  const meIsCollaborator = useMemo(
    () => isCollaborator(userEmail, issue),
    [userEmail, issue]
  );
  const roleKnown = meIsInitiator || meIsCollaborator;
  const roleLabel = meIsInitiator
    ? 'initiator'
    : meIsCollaborator
    ? 'collaborator'
    : 'unknown';

  // Mark status as implemented if we are here
  useEffect(() => {
    if (!sessionId || !roleKnown) return;
    (async () => {
      try {
        await client.models.Issue.update(
          { id: sessionId, status: 'implemented' as any },
          { authMode: 'userPool' }
        );
      } catch {
        /* no-op */
      }
    })();
  }, [sessionId, roleKnown]);

  /** ============ Fetch MY DCS Mode ============ */
  useEffect(() => {
    if (!userEmail) return;
    const emailLower = lower(userEmail);
    (async () => {
      try {
        const { data } = await client.models.Profile.list({
          filter: { emailLower: { eq: emailLower } },
          authMode: 'userPool',
        });
        if (mounted.current && data && data.length > 0) {
          setMyDcsMode(data[0].dcsMode ?? null);
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [userEmail]);

  /** ============ Per-role readiness derivations ============ */
  const iStrats = coerceStrings((issue as any)?.initiatorStrategies as any);
  const cStrats = coerceStrings((issue as any)?.collaboratorStrategies as any);
  const iTips = coerceStrings((issue as any)?.initiatorCommunicationTips as any);
  const cTips = coerceStrings((issue as any)?.collaboratorCommunicationTips as any);
  const iEmo = coerceStrings((issue as any)?.initiatorEmotionalTips as any);
  const cEmo = coerceStrings((issue as any)?.collaboratorEmotionalTips as any);

  // Check if strategies are already generated
  const strategiesGenerated = iStrats.length > 0 || cStrats.length > 0;

  const myStrats = meIsInitiator ? iStrats : cStrats;
  const myTips = meIsInitiator ? iTips : cTips;
  const myEmo = meIsInitiator ? iEmo : cEmo;

  const partnerStrats = meIsInitiator ? cStrats : iStrats;
  const partnerTips = meIsInitiator ? cTips : iTips;
  const partnerEmo = meIsInitiator ? cEmo : iEmo;

  const haveAnyForMe =
    myStrats.length > 0 || myTips.length > 0 || myEmo.length > 0;
  const partnerReady =
    partnerStrats.length > 0 ||
    partnerTips.length > 0 ||
    partnerEmo.length > 0;
  
  // NOTE: This logic was in previous code, still valid for UI readiness
  // but we primarily use myDcsMode / partnerDcsMode for the "Waiting" alert now.

  /** ============ Profile subscription + enqueue ============ */
  const otherEmail = useMemo(() => {
    if (!issue) return '';
    return meIsInitiator
      ? safe(issue.collaboratorEmail)
      : meIsCollaborator
      ? safe(issue.initiatorEmail)
      : '';
  }, [issue, meIsInitiator, meIsCollaborator]);

  useEffect(() => {
    if (!otherEmail || !sessionId) return;
    const emailLower = lower(otherEmail);
    if (!emailLower) return;

    const observable = (client.models.Profile as any).observeQuery?.({
      filter: { emailLower: { eq: emailLower } },
      authMode: 'userPool',
    });
    if (!observable?.subscribe) return;

    const sub = observable.subscribe({
      next: async (snapshot: any) => {
        const items: Profile[] = Array.isArray(snapshot?.items)
          ? snapshot.items
          : [];
        const row = items[0];
        const otherDcs = safe(row?.dcsMode);

        if (mounted.current) {
          setPartnerDcsMode(otherDcs || null);
        }

        // Trigger Generation Logic:
        // 1. I must have DCS (myDcsMode)
        // 2. Partner must have DCS (otherDcs)
        // 3. Strategies must NOT be generated yet
        // 4. Must not be enqueued yet
        if (
          myDcsMode && 
          otherDcs &&
          !strategiesGenerated &&
          PROPOSE_STRATEGIES_ENQUEUE &&
          !enqueuedRef.current &&
          issue?.id
        ) {
          try {
            console.log("Both parties have DCS. Enqueuing strategy generation...");
            enqueuedRef.current = true;
            const res = await fetch(PROPOSE_STRATEGIES_ENQUEUE, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ issueId: issue.id }),
            });
            if (!res.ok) {
              enqueuedRef.current = false;
            }
          } catch {
            enqueuedRef.current = false;
          }
        }
      },
      error: (_e: any) => {},
    });

    return () => sub.unsubscribe();
  }, [otherEmail, sessionId, strategiesGenerated, issue?.id, myDcsMode]);

  /** ============ Polling fallback to surface strategies reliably ============ */
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempt = 0;
    const MAX_ATTEMPTS = 30;
    const BASE_MS = 2500;
    const MULTIPLIER = 1.25;
    const MAX_MS = 15000;

    function hasBoth(i: Issue | null) {
      if (!i) return false;
      const iS = Array.isArray((i as any)?.initiatorStrategies) ? (i as any).initiatorStrategies : [];
      const cS = Array.isArray((i as any)?.collaboratorStrategies) ? (i as any).collaboratorStrategies : [];
      // Simple check if strategies exist
      return iS.length > 0 || cS.length > 0;
    }

    async function tick() {
      if (cancelled) return;

      if (hasBoth(latestIssueRef.current)) return;

      attempt += 1;
      try {
        const data = (await client.models.Issue.get(
          { id: sessionId },
          { authMode: 'userPool' }
        )) as unknown;
        const next = unwrap<Issue>(data);
        if (next) {
          setIssue(next);
          latestIssueRef.current = next;

          if (hasBoth(next)) return;
        }
      } catch {
        // ignore poll errors
      }

      if (!cancelled && attempt < MAX_ATTEMPTS) {
        const delay = Math.min(
          BASE_MS * Math.pow(MULTIPLIER, attempt - 1),
          MAX_MS
        );
        setTimeout(tick, delay);
      }
    }

    const initialHasBoth = hasBoth(latestIssueRef.current);
    if (!initialHasBoth) setTimeout(tick, BASE_MS);

    const forceTimer = setTimeout(() => {
      if (!cancelled) {
        readIssue(false).catch(() => {});
      }
    }, 30000);

    function onVis() {
      if (document.visibilityState === 'visible') {
        readIssue(false).catch(() => {});
      }
    }
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearTimeout(forceTimer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [sessionId]);

  /** ============ Derived UI ============ */
  const initiatorFirst = (issue?.initiatorFirstName ?? '').trim();
  const collaboratorFirst = (issue?.collaboratorFirstName ?? '').trim();
  const otherPartyFirst = meIsInitiator
    ? collaboratorFirst
    : meIsCollaborator
    ? initiatorFirst
    : 'Partner';

  const neutralSolution = useMemo(
    () => getNeutralSolutionFromIssue(issue),
    [issue]
  );

  // New UI Status Flags
  const waitingForPartnerDcs = myDcsMode && !partnerDcsMode;
  const waitingForStrategies = myDcsMode && partnerDcsMode && !haveAnyForMe;

  /** ============ Render ============ */
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-3">Loading…</div>
      </Container>
    );
  }
  if (error || !issue) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error ?? 'Session not found.'}</Alert>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={() =>
              navigate(
                `/session/${encodeURIComponent(sessionId)}`
              )
            }
          >
            Back to Session
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate('/session')}
          >
            My Sessions
          </Button>
        </div>
      </Container>
    );
  }
  if (!userEmail || !roleKnown) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">
          Please sign in as the invited participant to continue.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card className="mx-auto" style={{ maxWidth: 900 }}>
        <Card.Header as="h5">
          Ways to Implement the Solution. These are suggestions, not requirements
        </Card.Header>
        <Card.Body>
          {/* Greeting + relationship context */}
          <div className="mb-3">
            <div className="fw-semibold">
              Hi{firstName ? `, ${firstName}` : ''}! 👋
            </div>
            {otherPartyFirst && (
              <div className="text-muted small">
                You're the <strong>{roleLabel}</strong> and you're working with{' '}
                <strong>{otherPartyFirst}</strong>.
              </div>
            )}
            {/* Agreed Issue display */}
            {isNonEmptyString(
              (issue as any)?.agreedIssue
            ) && (
              <div className="mt-2 text-muted small">
                <span className="fw-semibold">
                  Agreed Issue:{' '}
                </span>
                {(issue as any).agreedIssue}
              </div>
            )}

            {/* Neutral Solution */}
            {neutralSolution && (
              <Card
                className="mt-3 mb-1"
                bg="light"
                text="dark"
              >
                <Card.Header className="fw-semibold">
                  Best Solution
                </Card.Header>
                <Card.Body>
                  <h6 className="mb-1">
                    {neutralSolution.title}
                  </h6>
                  {neutralSolution.summary && (
                    <p className="text-muted mb-2">
                      {neutralSolution.summary}
                    </p>
                  )}
                  {Array.isArray(
                    neutralSolution.steps
                  ) &&
                    neutralSolution.steps.length > 0 && (
                      <>
                        <div className="small text-muted mb-2">
                          Suggested steps
                        </div>
                        <ListGroup className="mb-2">
                          {neutralSolution.steps.map(
                            (s, i) => (
                              <ListGroup.Item
                                key={`neutral-step-main-${i}`}
                              >
                                {s}
                              </ListGroup.Item>
                            )
                          )}
                        </ListGroup>
                      </>
                    )}
                </Card.Body>
              </Card>
            )}
          </div>

          {/* === STATUS LOGIC START === */}
          {waitingForPartnerDcs ? (
            /* CASE 1: I have DCS, Partner DOES NOT */
             <Alert variant="warning" className="text-center py-4 mb-3">
                <Spinner animation="grow" size="sm" className="me-2" variant="warning" />
                <strong>Waiting for {otherPartyFirst}</strong>
                <div className="mt-2">
                  {otherPartyFirst} has not completed their Conflict Style assessment yet. 
                  We cannot generate personalized implementation strategies until both parties have completed it.
                </div>
             </Alert>
          ) : (
            /* CASE 2: Partner ready (or I'm not ready/strategies exist) */
            <>
              {/* Strategies/tips */}
              {haveAnyForMe ? (
                <div className="mb-3">
                  <h6 className="mb-2">
                    Ways to implement the solution
                  </h6>
                  <div className="row">
                    {/* My panel */}
                    <div className="col-md-6">
                      <Card
                        className="mb-3"
                        bg="light"
                        text="dark"
                      >
                        <Card.Header className="fw-semibold">
                          {meIsInitiator
                            ? initiatorFirst
                            : collaboratorFirst}
                        </Card.Header>
                        <Card.Body>
                          {myStrats.length > 0 && (
                            <ListGroup className="mb-2">
                              {myStrats.map((s, i) => (
                                <ListGroup.Item
                                  key={`me-strat-${i}`}
                                >
                                  {s}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          )}
                          {myTips.length > 0 && (
                            <>
                              <div className="small text-muted mb-1">
                                Communication Tips
                              </div>
                              <ListGroup className="mb-2">
                                {myTips.map((s, i) => (
                                  <ListGroup.Item
                                    key={`me-tip-${i}`}
                                  >
                                    {s}
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                            </>
                          )}
                          {myEmo.length > 0 && (
                            <>
                              <div className="small text-muted mb-1">
                                Emotional Navigation
                              </div>
                              <ListGroup>
                                {myEmo
                                  .slice(0, 2)
                                  .map((s, i) => (
                                    <ListGroup.Item
                                      key={`me-emo-${i}`}
                                    >
                                      {s}
                                    </ListGroup.Item>
                                  ))}
                              </ListGroup>
                            </>
                          )}
                          {myStrats.length === 0 &&
                            myTips.length === 0 &&
                            myEmo.length === 0 && (
                              <div className="text-muted small">
                                No items yet.
                              </div>
                            )}
                        </Card.Body>
                      </Card>
                    </div>

                    {/* Partner panel */}
                    <div className="col-md-6">
                      <Card
                        className="mb-3"
                        bg="light"
                        text="dark"
                      >
                        <Card.Header className="fw-semibold">
                          {meIsInitiator
                            ? collaboratorFirst
                            : initiatorFirst}
                        </Card.Header>
                        <Card.Body>
                          {partnerReady ? (
                            <>
                              {partnerStrats.length > 0 && (
                                <ListGroup className="mb-2">
                                  {partnerStrats.map(
                                    (s, i) => (
                                      <ListGroup.Item
                                        key={`partner-strat-${i}`}
                                      >
                                        {s}
                                      </ListGroup.Item>
                                    )
                                  )}
                                </ListGroup>
                              )}
                              {partnerTips.length > 0 && (
                                <>
                                  <div className="small text-muted mb-1">
                                    Communication Tips
                                  </div>
                                  <ListGroup className="mb-2">
                                    {partnerTips.map(
                                      (s, i) => (
                                        <ListGroup.Item
                                          key={`partner-tip-${i}`}
                                        >
                                          {s}
                                        </ListGroup.Item>
                                      )
                                    )}
                                  </ListGroup>
                                </>
                              )}
                              {partnerEmo.length > 0 && (
                                <>
                                  <div className="small text-muted mb-1">
                                    Emotional Navigation
                                  </div>
                                  <ListGroup>
                                    {partnerEmo
                                      .slice(0, 2)
                                      .map((s, i) => (
                                        <ListGroup.Item
                                          key={`partner-emo-${i}`}
                                        >
                                          {s}
                                        </ListGroup.Item>
                                      ))}
                                  </ListGroup>
                                </>
                              )}
                              {partnerStrats.length === 0 &&
                                partnerTips.length === 0 &&
                                partnerEmo.length === 0 && (
                                  <div className="text-muted small">
                                    No items yet.
                                  </div>
                                )}
                            </>
                          ) : (
                            <div className="text-muted fst-italic">
                               Hidden until partner views them.
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </div>
              ) : (
                <Alert
                  variant="info"
                  className="mb-3"
                >
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  {/* If both ready but nothing here, we are generating. Else just preparing. */}
                  {waitingForStrategies ? 
                     "Both parties are ready. Preparing your personalized strategies..." : 
                     "Preparing your strategies and tips..."}
                </Alert>
              )}
            </>
          )}

          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => window.print()}
            >
              Print / Save PDF
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/session')}
            >
              My Sessions
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate(`/init`)}
            >
              Start New Session
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}